// src/pages/Workouts.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Form,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import "../css/workouts.css";
import Banner from "../components/Banner";
import { API_BASE_URL } from "../config";
const resolveCoverUrl = (u) => {
  if (!u) return `${API_BASE_URL}/uploads/covers/placeholder.jpg`;
  if (/^https?:\/\//i.test(u)) return u; // già assoluta
  if (u.startsWith("/uploads/")) return `${API_BASE_URL}${u}`; // backend uploads
  return u; // fallback
};

console.log("ENV =", process.env.REACT_APP_API_BASE_URL);

export default function Workouts() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI
  const [query, setQuery] = useState("");
  const [level, setLevel] = useState("all");
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [minWeeks, setMinWeeks] = useState("");
  const [maxWeeks, setMaxWeeks] = useState("");
  const [minSessions, setMinSessions] = useState("");
  const [sortBy, setSortBy] = useState("title-asc");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/workouts`);
        const data = res.ok ? await res.json() : [];
        if (!cancelled) setPlans(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setPlans([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    let list = [...plans];
    const q = query.trim().toLowerCase();

    if (q) {
      list = list.filter(
        (p) =>
          (p.title || "").toLowerCase().includes(q) ||
          (p.subtitle || "").toLowerCase().includes(q)
      );
    }
    if (level !== "all")
      list = list.filter((p) => (p.level || "").toLowerCase() === level);
    if (onlyAvailable)
      list = list.filter((p) => (p.status || "").toLowerCase() === "available");
    if (minWeeks !== "")
      list = list.filter((p) => Number(p.weeks) >= Number(minWeeks));
    if (maxWeeks !== "")
      list = list.filter((p) => Number(p.weeks) <= Number(maxWeeks));
    if (minSessions !== "")
      list = list.filter(
        (p) => Number(p.sessionsPerWeek) >= Number(minSessions)
      );

    switch (sortBy) {
      case "weeks-desc":
        list.sort((a, b) => Number(b.weeks) - Number(a.weeks));
        break;
      case "weeks-asc":
        list.sort((a, b) => Number(a.weeks) - Number(b.weeks));
        break;
      case "sessions-desc":
        list.sort(
          (a, b) => Number(b.sessionsPerWeek) - Number(a.sessionsPerWeek)
        );
        break;
      default:
        list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    }
    return list;
  }, [
    plans,
    query,
    level,
    onlyAvailable,
    minWeeks,
    maxWeeks,
    minSessions,
    sortBy,
  ]);

  const levels = useMemo(() => {
    const set = new Set(
      plans.map((p) => (p.level || "").toLowerCase()).filter(Boolean)
    );
    return ["all", ...Array.from(set)];
  }, [plans]);

  return (
    <main className="py-5 workouts-page">
      <Container>
        <header className="text-center mb-4">
          <h2 className="fw-bold text-light">WORKOUT PLANS</h2>
        </header>

        {/* FILTER BAR */}
        <section className="filter-card mb-4">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={4}>
              <Form.Label className="filter-label">Search</Form.Label>
              <Form.Control
                type="search"
                placeholder="e.g. Full Body, Hypertrophy…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </Col>

            <Col xs={6} md={2}>
              <Form.Label className="filter-label">Level</Form.Label>
              <Form.Select
                value={level}
                onChange={(e) => setLevel(e.target.value)}
              >
                {levels.map((lv) => (
                  <option key={lv} value={lv}>
                    {lv === "all" ? "All" : lv[0].toUpperCase() + lv.slice(1)}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2}>
              <Form.Label className="filter-label">Min weeks</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={minWeeks}
                onChange={(e) => setMinWeeks(e.target.value)}
                placeholder="e.g. 4"
              />
            </Col>

            <Col xs={6} md={2}>
              <Form.Label className="filter-label">Max weeks</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={maxWeeks}
                onChange={(e) => setMaxWeeks(e.target.value)}
                placeholder="e.g. 8"
              />
            </Col>

            <Col xs={6} md={2}>
              <Form.Label className="filter-label">Min sessions/wk</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={minSessions}
                onChange={(e) => setMinSessions(e.target.value)}
                placeholder="e.g. 3"
              />
            </Col>

            <Col xs={12} md={4}>
              <Form.Label className="filter-label">Sort by</Form.Label>
              <Form.Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="title-asc">Title (A→Z)</option>
                <option value="weeks-desc">Weeks (high→low)</option>
                <option value="weeks-asc">Weeks (low→high)</option>
                <option value="sessions-desc">Sessions/week (high→low)</option>
              </Form.Select>
            </Col>

            <Col xs={12} md="auto" className="d-flex align-items-center gap-3">
              <Form.Check
                type="switch"
                id="onlyAvailable"
                label="Only available"
                checked={onlyAvailable}
                onChange={(e) => setOnlyAvailable(e.target.checked)}
                className="m-0"
              />
              <Button
                variant="outline-secondary"
                className="btn-reset m-0"
                onClick={() => {
                  setQuery("");
                  setLevel("all");
                  setOnlyAvailable(false);
                  setMinWeeks("");
                  setMaxWeeks("");
                  setMinSessions("");
                  setSortBy("title-asc");
                }}
              >
                Reset filters
              </Button>
            </Col>

            <Col xs="12" className="text-end text-muted small">
              {!loading && (
                <span>
                  {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                </span>
              )}
            </Col>
          </Row>
        </section>

        {/* LIST */}
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center py-5">No plans found.</p>
        ) : (
          <Row className="g-4">
            {filtered.map((plan) => {
              const available =
                (plan.status || "").toLowerCase() === "available";
              const cover = resolveCoverUrl(plan.coverUrl);

              const priceStr =
                typeof plan.price === "number"
                  ? `${plan.price} ${plan.currency || "EUR"}`
                  : null;
              const slug = (plan.slug || plan.id || "").toString();

              return (
                <Col key={plan.id || plan.slug} xs={12} md={6} lg={4}>
                  <Card className="h-100 workout-card shadow-sm">
                    {cover && (
                      <Card.Img
                        variant="top"
                        src={cover}
                        alt={plan.title}
                        className="workout-cover"
                      />
                    )}
                    <Card.Body className="d-flex flex-column">
                      <div className="mb-2">
                        <Card.Title className="mb-1">{plan.title}</Card.Title>
                        {plan.subtitle && (
                          <Card.Subtitle className="card-subtitle">
                            {plan.subtitle}
                          </Card.Subtitle>
                        )}
                      </div>
                      <div className="workout-badges mb-3">
                        {plan.level && (
                          <Badge className="pill">Level: {plan.level}</Badge>
                        )}
                        {!!plan.weeks && (
                          <Badge className="pill">{plan.weeks} weeks</Badge>
                        )}
                        {!!plan.sessionsPerWeek && (
                          <Badge className="pill">
                            {plan.sessionsPerWeek} sessions/wk
                          </Badge>
                        )}
                        {!available && (
                          <Badge className="pill pill-muted">Coming soon</Badge>
                        )}
                      </div>
                      {priceStr && (
                        <div className="mb-3 fw-semibold text-dark">
                          {priceStr}
                        </div>
                      )}
                      <div className="mt-auto">
                        <Button
                          as={Link}
                          to={`/workouts/${slug}`}
                          variant="primary"
                          className="w-100"
                        >
                          View details
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>
      <Banner className="mt-2" />
    </main>
  );
}
